import {
  Layout,
  Button,
  Avatar,
  Space,
  Dropdown,
  Popover,
  theme,
  message,
  Spin,
} from "antd";

import {
  FileSearchOutlined,
  DownloadOutlined,
  LoadingOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
  AppstoreOutlined,
  CreditCardOutlined,
  DownOutlined,
  CheckCircleOutlined,
  CrownOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  BgColorsOutlined,
} from "@ant-design/icons";

import { useNavigate, useLocation } from "react-router-dom";
import { useResumeStore } from "../../store/resumeStore";
import { isResumeEmpty } from "./mapParsedResumeToState";
import { getAuth, signOut } from "firebase/auth";
import { useEffect, useState } from "react";
import ATSChecker from "../NewResume/ATSChecker";
import HistoryDrawer from "../NewResume/HistoryDrawer";
import { ReactComponent as Logo } from "../../Images/resumiq-logo.svg";
import useViewportWidth from "../../hooks/useViewportWidth";
import useSubscriptionStatus from "../Subscription/useSubscriptionStatus";
import {
  logTemplateSelection,
  withTemplateIdInSearch,
} from "../NewResume/templateSelection";
import {
  COLOR_THEME_OPTIONS,
  COLOR_THEME_PRESETS,
} from "../NewResume/colorThemes";
const { Header } = Layout;

function cleanText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function getDocumentTitle(resume) {
  const role = cleanText(
    resume?.roleTitle ||
      resume?.role ||
      resume?.headline ||
      resume?.designation ||
      resume?.jobTitle,
  );
  const name = cleanText(resume?.name);

  if (role) {
    return /resume$/i.test(role) ? role : `${role} Resume`;
  }

  if (name) {
    return `${name} Resume`;
  }

  return "Untitled Resume";
}

function formatShortDate(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-IN", {
    month: "short",
    day: "numeric",
  }).format(date);
}

function getInitials(value) {
  const parts = cleanText(value).split(/\s+/).filter(Boolean);
  if (!parts.length) return "MT";
  return parts.slice(0, 2).map((part) => part[0]).join("").toUpperCase();
}

export default function AppHeader({
  theme: themeMode,
  onToggleTheme,
  userName,
  colorTheme = "blue",
  setColorTheme = () => {},
}) {

  /* ================= SCREEN WIDTH ================= */

  const viewportWidth = useViewportWidth();
  const isMobile = viewportWidth <= 768;
  const isSmallScreen = viewportWidth <= 1024;
  const isVerySmallScreen = viewportWidth <= 420;
  const [showATS, setShowATS] = useState(false);
  const { subscription } = useSubscriptionStatus();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [builderReviewMeta, setBuilderReviewMeta] = useState(() => ({
    count: Number(sessionStorage.getItem("cvcanvas:builder-review-count") || 0),
    isImportedUpload: sessionStorage.getItem("isUpload") === "yes",
  }));
  const { exportLoading, setExportLoading } = useResumeStore();

  useEffect(() => {
    const handleBuilderReviewSummary = (event) => {
      setBuilderReviewMeta({
        count: Number(event.detail?.count || 0),
        isImportedUpload: Boolean(event.detail?.isImportedUpload),
      });
    };

    window.addEventListener("cvcanvas:builder-review-summary", handleBuilderReviewSummary);

    return () => {
      window.removeEventListener("cvcanvas:builder-review-summary", handleBuilderReviewSummary);
    };
  }, []);

  const navigate = useNavigate();
  const { token } = theme.useToken();
  const resetResume = useResumeStore((s) => s.resetResume);
  const currentResume = useResumeStore((s) => s.resume);
  const selectedTemplateId = useResumeStore((s) => s.selectedTemplateId);
  const [messageApi, contextHolder] = message.useMessage();
  const location = useLocation();
  const path = location?.pathname;
  const previewBlocked = isResumeEmpty(currentResume);
  const isResumeWorkspace = [
    "/resume/new",
    "/export",
    "/resume/import",
    "/resume/review",
    "/templates",
  ].includes(path);
  const isTemplatePickerOpen =  new URLSearchParams(location.search).get("templates") === "open";
  const templateSearch = withTemplateIdInSearch("", selectedTemplateId);
  const documentTitle = getDocumentTitle(currentResume);
  const documentSubtitle = cleanText(currentResume?.name) || "Draft document";
  const hasResumeContent = !previewBlocked;
  const subscriptionExpiresAt = formatShortDate(subscription?.subscriptionExpiresAt);
  const isPremium = Boolean(subscription?.active);
  const isExpired = subscription?.status === "expired";
  const planLabel = isPremium ? "Premium" : isExpired ? "Expired" : "Free";
  const planMeta = isPremium
    ? subscription?.daysRemaining != null
      ? `${subscription.daysRemaining} day${subscription.daysRemaining === 1 ? "" : "s"} left`
      : subscriptionExpiresAt
        ? `Expires ${subscriptionExpiresAt}`
        : "Active"
    : isExpired
      ? "Renew access"
      : "Upgrade";
  const userInitials = getInitials(userName);
  const showBuilderReviewNotice =
    path === "/resume/new" &&
    !isSmallScreen &&
    (builderReviewMeta.isImportedUpload || builderReviewMeta.count > 0);
    
const builderReviewNotice = builderReviewMeta.isImportedUpload
  ? builderReviewMeta.count > 0
    ? `AI found ${builderReviewMeta.count} field${builderReviewMeta.count > 1 ? "s" : ""} to verify. AI may have misread some details. Parser may have missed some values. Please validate the fields carefully.`
    : "AI may have misread some details. Parser may have missed some values. Please validate the fields carefully."
  : builderReviewMeta.count > 0
    ? `AI found ${builderReviewMeta.count} field${builderReviewMeta.count > 1 ? "s" : ""}. AI may have misread ${builderReviewMeta.count === 1 ? "a detail" : "some details"}. Please review the ATS warnings field${builderReviewMeta.count > 1 ? "s" : ""}.`
    : "";

  /* ================= LOGOUT ================= */

  const handleLogout = async () => {
    const auth = getAuth();

    try {
      await signOut(auth);
      console.log("User logged out");
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error.message);
    }
  };

  const handleTemplatePickerToggle = () => {
    if (isSmallScreen && path === "/export") {
      messageApi.info("Template gallery is available only on larger screens.");
      return;
    }

    const params = new URLSearchParams(location.search);

    if (isTemplatePickerOpen) {
      params.delete("templates");
    } else {
      params.set("templates", "open");
    }

    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      {
        replace: true,
        state: location.state,
      }
    );
  };

  const handleDownloadAction = (format = "pdf") => {
    if (previewBlocked) {
      messageApi.warning("Please fill resume details before Preview & Download.");
      return;
    }

    setExportLoading(true);
    window.dispatchEvent(
      new CustomEvent("cvcanvas:export-download", {
        detail: {
          format,
        },
      })
    );
  };

  const handleBackClick = () => {
    if (path === "/export") {
      navigate({
        pathname: "/resume/new",
        search: templateSearch,
      });
      return;
    }

    resetResume();
    navigate("/dashboard");
  };

  const userMenu = [
  {
    key: "History",
    icon: <UserOutlined />,
    label: "History",
    onClick: () => setHistoryOpen(true)
  },
  {
    key: "subscription",
    icon: <CreditCardOutlined />,
    label: "Subscription & Billing",
    onClick: () => navigate("/subscription")
  },
  {
    key: "settings",
    icon: <SettingOutlined />,
    label: "Settings"
  },
  {
    type: "divider"
  },
  {
    key: "logout",
    icon: <LogoutOutlined />,
    label: "Logout",
    danger: true,
    onClick: handleLogout
  }
];

  const subscriptionMenu = [
    {
      key: "plan",
      icon: <CreditCardOutlined />,
      label: isPremium
        ? `Premium${subscriptionExpiresAt ? ` · Expires ${subscriptionExpiresAt}` : ""}`
        : isExpired
          ? "Renew Premium"
          : "Upgrade to Premium",
      onClick: () => navigate("/subscription"),
    },
    {
      key: "billing",
      icon: <CreditCardOutlined />,
      label: "Manage Billing",
      onClick: () => navigate("/subscription"),
    },
  ];

  const downloadMenuItems = [
    {
      key: "pdf",
      icon: <FilePdfOutlined />,
      label: "Download PDF",
    },
    {
      key: "docx",
      icon: <FileWordOutlined />,
      label: "Download DOCX",
    },
    {
      key: "doc",
      icon: <FileWordOutlined />,
      label: "Download DOC",
    },
  ];

  const selectedThemeSwatch =
    COLOR_THEME_PRESETS[colorTheme]?.swatch || COLOR_THEME_PRESETS.blue.swatch;

  const themePresetContent = (
    <div
      style={{
        width: 236,
        display: "grid",
        gap: 12,
        padding: 4,
      }}
    >
      <div style={{ display: "grid", gap: 3 }}>
        <strong style={{ color: "#0f172a", fontSize: 13 }}>Theme Presets</strong>
        <span style={{ color: "#64748b", fontSize: 12 }}>
          Updates preview and PDF colors.
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 10,
        }}
      >
        {COLOR_THEME_OPTIONS.map((color) => {
          const swatch = COLOR_THEME_PRESETS[color].swatch;
          const isActive = colorTheme === color;

          return (
            <button
              key={color}
              type="button"
              onClick={() => setColorTheme(color)}
              aria-label={`Select ${color} theme`}
              aria-pressed={isActive}
              style={{
                width: 30,
                height: 30,
                borderRadius: 999,
                border: isActive
                  ? "2px solid #0f172a"
                  : "1px solid rgba(148, 163, 184, 0.5)",
                background: swatch,
                boxShadow: isActive ? `0 0 0 3px ${swatch}30` : "none",
                cursor: "pointer",
              }}
            />
          );
        })}
      </div>
    </div>
  );

  const showBackButton = [
    "/resume/new",
    "/export",
    "/resume/import",
    "/resume/review",
    "/templates"
  ].includes(location.pathname);

  return (
    <>
      {contextHolder}

      <Header
        style={{
          height: isMobile ? 62 : 74,
          padding: isMobile ? "0 12px" : "0 24px",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          alignItems: "center",
          columnGap: isMobile ? 10 : 20,
          backdropFilter: "blur(18px)",
          background: isResumeWorkspace
            ? "linear-gradient(180deg, rgba(8, 13, 24, 0.96), rgba(15, 23, 42, 0.9))"
            : token.colorBgContainer,
          borderBottom: isResumeWorkspace
            ? "1px solid rgba(148, 163, 184, 0.16)"
            : `1px solid ${token.colorBorder}`,
          boxShadow: isResumeWorkspace
            ? "0 16px 40px rgba(2, 6, 23, 0.34)"
            : "none",
          position: "sticky",
          top: 0,
          zIndex: 1000,
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 9 : 14,
            minWidth: 0,
            justifySelf: "start",
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          {showBackButton && (
            <Button
              icon={<ArrowLeftOutlined />}
              type="text"
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                color: isResumeWorkspace ? "#e2e8f0" : undefined,
                flexShrink: 0,
              }}
              onClick={handleBackClick}
            />
          )}

          {!isSmallScreen ? (
            <Logo
              aria-label="ResumeIQ"
              style={{
                width: 138,
                height: "auto",
                color: isResumeWorkspace ? "#f8fafc" : token.colorTextHeading,
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              style={{
                color: isResumeWorkspace ? "#f8fafc" : token.colorTextHeading,
                fontSize: 18,
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              ResumeIQ
            </div>
          )}

          {isResumeWorkspace && !isVerySmallScreen ? (
            <div
              style={{
                display: "grid",
                gap: 3,
                minWidth: 0,
                paddingLeft: isSmallScreen ? 0 : 10,
                borderLeft: isSmallScreen ? "none" : "1px solid rgba(148, 163, 184, 0.18)",
              }}
            >
              <div
                title={documentTitle}
                style={{
                  color: "#f8fafc",
                  fontSize: isMobile ? 13 : 15,
                  fontWeight: 750,
                  lineHeight: 1.15,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: isSmallScreen ? 190 : 340,
                }}
              >
                {documentTitle}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  minWidth: 0,
                  color: "#94a3b8",
                  fontSize: 12,
                  lineHeight: 1,
                }}
              >
                <span
                  title={documentSubtitle}
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: isSmallScreen ? 96 : 160,
                  }}
                >
                  {documentSubtitle}
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    color: hasResumeContent ? "#86efac" : "#cbd5e1",
                    whiteSpace: "nowrap",
                  }}
                >
                  <CheckCircleOutlined />
                  {hasResumeContent ? "Saved locally" : "Draft"}
                </span>
              </div>
            </div>
          ) : null}

          {showBuilderReviewNotice && !isSmallScreen ? (
          <div
            style={{
              minWidth: 0,
              maxWidth: 360,
              padding: "7px 12px",
              borderRadius: 999,
              border: "1px solid rgba(248, 113, 113, 0.34)",
              background: "linear-gradient(180deg, rgba(127, 29, 29, 0.2), rgba(127, 29, 29, 0.12))",
              color: "#fee2e2",
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.2,
              boxShadow: "0 16px 34px rgba(2, 6, 23, 0.22)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={builderReviewNotice}
          >
            {builderReviewNotice}
          </div>
          ) : null}
        </div>

        <HistoryDrawer
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          userName={userName}
        />

        <Space
          size={isMobile ? 6 : isSmallScreen ? 8 : 10}
          style={{
            flexWrap: "nowrap",
            justifyContent: "flex-end",
            minWidth: 0,
            maxWidth: "100%",
            overflow: "hidden",
            justifySelf: "end",
          }}
        >
          {path === "/resume/new" && (
            <Button
              icon={<FileSearchOutlined />}
              style={{
                height: isSmallScreen ? 34 : 36,
                borderRadius: isSmallScreen ? 8 : 8,
                fontWeight: 500,
                fontSize: isSmallScreen ? 13 : 14,
                paddingInline: isSmallScreen ? 12 : 14,
                minWidth: isSmallScreen ? (isVerySmallScreen ? 76 : 106) : undefined,
                border: "1px solid rgba(148, 163, 184, 0.16)",
                background: isResumeWorkspace
                  ? "rgba(15, 23, 42, 0.72)"
                  : token.colorBgElevated,
                color: isResumeWorkspace ? "#e2e8f0" : undefined,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              onClick={() => setShowATS(true)}
            >
              {isVerySmallScreen ? "ATS" : "ATS"}
            </Button>
          )}

          {path === "/export" && !isSmallScreen && (
            <Button
              icon={<AppstoreOutlined />}
              style={{
                height: 36,
                borderRadius: 9,
                fontWeight: 600,
                border: "1px solid rgba(148, 163, 184, 0.16)",
                background: isTemplatePickerOpen
                  ? "rgba(59, 130, 246, 0.18)"
                  : "rgba(15, 23, 42, 0.62)",
                color: "#e2e8f0",
              }}
              onClick={handleTemplatePickerToggle}
            >
              Templates
            </Button>
          )}

          {isResumeWorkspace && !isSmallScreen ? (
            <Popover
              content={themePresetContent}
              placement="bottomRight"
              trigger="click"
              overlayInnerStyle={{
                borderRadius: 14,
                boxShadow: "0 18px 46px rgba(15, 23, 42, 0.22)",
              }}
            >
              <Button
                icon={<BgColorsOutlined />}
                style={{
                  height: 36,
                  borderRadius: 9,
                  fontWeight: 600,
                  border: "1px solid rgba(148, 163, 184, 0.16)",
                  background: "rgba(15, 23, 42, 0.62)",
                  color: "#e2e8f0",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 999,
                    background: selectedThemeSwatch,
                    boxShadow: `0 0 0 2px ${selectedThemeSwatch}30`,
                  }}
                />
                Theme
              </Button>
            </Popover>
          ) : null}

          {path === "/resume/new" && (
            <Button
              type="primary"
              size="large"
              icon={isSmallScreen ? <DownloadOutlined /> : <EyeOutlined />}
              style={{
                height: isSmallScreen ? 36 : 40,
                borderRadius: isSmallScreen ? 8 : 10,
                fontWeight: 500,
                fontSize: isSmallScreen ? 13 : 14,
                paddingInline: isSmallScreen ? 12 : 18,
                minWidth: isSmallScreen ? (isVerySmallScreen ? 108 : 126) : undefined,
                background: `linear-gradient(135deg, ${token.colorPrimary}, #6366f1)`,
                border: "none",
                boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              disabled={previewBlocked || (isSmallScreen && exportLoading)}
              onClick={() => {
                if (previewBlocked) {
                  messageApi.warning(
                    "Please fill resume details before Preview & Download."
                  );
                  return;
                }

                if (isSmallScreen) {
                  window.dispatchEvent(
                    new CustomEvent("cvcanvas:builder-download", {
                      detail: { format: "pdf" },
                    }),
                  );
                  return;
                }

                logTemplateSelection("AppHeader:/resume/new:preview-click", {
                  selectedTemplateId,
                  path,
                });
                navigate(
                  {
                    pathname: "/export",
                    search: templateSearch,
                  },
                  {
                    state: {
                      templateId: selectedTemplateId,
                    },
                  },
                );
              }}
            >
              {isSmallScreen
                ? exportLoading
                  ? "Downloading..."
                  : "Download"
                : "Preview"}
            </Button>
          )}

          {path === "/export" && (
            <Dropdown
              menu={{
                items: downloadMenuItems,
                onClick: ({ key }) => handleDownloadAction(key),
              }}
              placement="bottomRight"
              trigger={["click"]}
            >
              <Button
                type="primary"
                disabled={previewBlocked || exportLoading}
                style={{
                  height: isSmallScreen ? 36 : 40,
                  borderRadius: isSmallScreen ? 8 : 10,
                  fontWeight: 500,
                  fontSize: isSmallScreen ? 13 : 14,
                  padding: isSmallScreen
                    ? `0 ${isVerySmallScreen ? 12 : 14}px`
                    : "0 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: `linear-gradient(135deg, ${token.colorPrimary}, #6366f1)`,
                  border: "none",
                  boxShadow: "0 10px 24px rgba(79, 70, 229, 0.28)",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {exportLoading ? (
                  <>
                    <Spin indicator={<LoadingOutlined spin />} size="small" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <DownloadOutlined />
                    Download
                    {!isVerySmallScreen ? <DownOutlined style={{ fontSize: 11 }} /> : null}
                  </>
                )}
              </Button>
            </Dropdown>
          )}

          {!isVerySmallScreen ? (
            <Dropdown
              menu={{ items: subscriptionMenu }}
              placement="bottomRight"
              trigger={["click"]}
            >
              <Button
                style={{
                  height: 40,
                  borderRadius: 12,
                  padding: isSmallScreen ? "0 10px" : "0 12px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  border: isPremium
                    ? "1px solid rgba(134, 239, 172, 0.34)"
                    : isExpired
                      ? "1px solid rgba(248, 113, 113, 0.34)"
                      : "1px solid rgba(148, 163, 184, 0.16)",
                  background: isPremium
                    ? "rgba(22, 163, 74, 0.12)"
                    : isExpired
                      ? "rgba(127, 29, 29, 0.16)"
                      : "rgba(15, 23, 42, 0.62)",
                  color: "#e2e8f0",
                  boxShadow: "none",
                }}
              >
                <CrownOutlined style={{ color: isPremium ? "#86efac" : "#cbd5e1" }} />
                {!isSmallScreen ? (
                  <span
                    style={{
                      display: "grid",
                      lineHeight: 1.05,
                      textAlign: "left",
                    }}
                  >
                    <strong style={{ fontSize: 12 }}>{planLabel}</strong>
                    <span style={{ color: "#94a3b8", fontSize: 11 }}>{planMeta}</span>
                  </span>
                ) : null}
                <DownOutlined style={{ fontSize: 10, color: "#94a3b8" }} />
              </Button>
            </Dropdown>
          ) : null}

          <Dropdown
            menu={{ items: userMenu }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Button
              style={{
                height: 40,
                borderRadius: 12,
                padding: isSmallScreen ? "0 8px" : "0 10px",
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                border: "1px solid rgba(148, 163, 184, 0.16)",
                background: isResumeWorkspace
                  ? "rgba(15, 23, 42, 0.62)"
                  : token.colorBgElevated,
                color: isResumeWorkspace ? "#e2e8f0" : token.colorText,
              }}
            >
              <Avatar
                size={28}
                style={{
                  background: "linear-gradient(135deg, #38bdf8, #6366f1)",
                  color: "#ffffff",
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                {userInitials}
              </Avatar>
              {!isSmallScreen ? <DownOutlined style={{ fontSize: 10, color: "#94a3b8" }} /> : null}
            </Button>
          </Dropdown>

        </Space>
        <ATSChecker
          resumeData={currentResume}
          setShowATS={setShowATS}
          showATS={showATS}
        />
      </Header>
    </>
  );
}
